import React from "react";
import TopBanner from "../components/TopBanner";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { ContentWrapper } from "./InsuranceGuidance";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";
import FAQ from "../components/FAQ";

const LifeInsurance = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Life Insurance Guide – Meaning, Types, Benefits & Tax Benefits in India | Wealthies";
  }, []);

  const faqData = [
    {
      question: "Is life insurance necessary for everyone?",
      answer:
        "Life insurance may be considered by individuals who have financial dependents or long-term financial responsibilities.",
    },
    {
      question: "Can life insurance provide tax benefits?",
      answer:
        "Certain life insurance policies may provide tax benefits under the Income Tax Act, subject to eligibility conditions.",
    },
    {
      question: "What happens if premiums are not paid?",
      answer:
        "Failure to pay premiums may affect policy continuation. Policy terms may vary depending on the insurer.",
    },
  ];

  return (
    <>
      <TopBanner content={"Life Insurance"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>Introduction</h2>
          <p>
            Life insurance is designed to provide financial protection to the
            policyholder’s family or dependents in case of unforeseen events. It
            plays an important role in long-term financial planning for many
            individuals and families.
          </p>
          <p>
            A life insurance policy typically provides a financial payout to the
            nominee if the insured person passes away during the policy term,
            subject to the terms and conditions of the policy.
          </p>
          <p>
            Different types of life insurance policies are available, and each
            type may serve different financial planning needs.
          </p>
          <p>
            Understanding how life insurance works can help individuals evaluate
            coverage options and prepare for future financial responsibilities.
          </p>

          <h2>
            What is <span>Life Insurance</span>
          </h2>
          <p>
            Life insurance is a financial protection tool designed to provide
            financial support to dependents in case something happens to the
            insured person.
          </p>
          <p>
            The policyholder pays premiums to the insurance company for the
            coverage. If the insured person passes away during the policy term,
            the nominee may receive the sum assured, subject to policy terms and
            conditions.
          </p>
          <p>
            Life insurance policies are often considered by individuals who want
            to help provide financial protection for their families.
          </p>

          <h2>
            Why <span>Life Insurance</span> is Important
          </h2>
          <p>
            Life insurance can help provide financial support to family members
            or dependents during difficult situations.
          </p>
          <p>Some reasons people consider life insurance include:</p>
          <Cards>
            <Card>
              <Icon className="fas fa-user-shield"></Icon>
              <CardText>
                Helping provide financial security for family members
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-users"></Icon>
              <CardText>
                Supporting dependents in managing daily living expenses
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-file-invoice-dollar"></Icon>
              <CardText>
                Assisting with repayment of financial obligations such as loans
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-balance-scale"></Icon>
              <CardText>
                Helping maintain financial stability for dependents
              </CardText>
            </Card>
          </Cards>
          <p>
            The decision to purchase insurance depends on individual financial
            planning goals and family responsibilities.
          </p>

          <h2>
            Eligibility for <span>Life Insurance</span> in India
          </h2>
          <p>
            Eligibility criteria for life insurance policies may vary between
            insurers. However, most insurance companies follow general
            guidelines.
          </p>

          <h5>Age Eligibility</h5>
          <p>
            Minimum entry age for most life insurance policies is usually around
            18 years.
          </p>
          <p>
            Maximum entry age may vary depending on the type of policy and
            insurer.
          </p>

          <h5>Income and Financial Eligibility</h5>
          <p>
            Insurance companies may assess income details to determine the
            suitable coverage amount.
          </p>
          <p>Applicants may need to submit documents such as:</p>
          <ul>
            <li>Salary slips</li>
            <li>Income tax returns</li>
            <li>Bank statements</li>
          </ul>

          <h5>Medical Assessment</h5>
          <p>
            Depending on the policy type, age, and coverage amount, medical
            examinations may be required before issuing the policy.
          </p>
          <p>
            Medical tests help insurers assess health conditions and risk
            factors.
          </p>

          <h2>
            Types of <span>Life Insurance</span> Policies
          </h2>
          <p>
            Several types of life insurance policies are available in India.
          </p>

          <Cards>
            <Card>
              <Icon className="fas fa-hourglass-half"></Icon>
              <CardTitle>Term Life Insurance</CardTitle>
              <CardText>
                Term life insurance provides coverage for a specific period and
                is designed mainly for financial protection.
              </CardText>
              <CardText>
                If the insured person passes away during the policy term, the
                nominee may receive the sum assured.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-piggy-bank"></Icon>
              <CardTitle>Endowment Policies</CardTitle>
              <CardText>
                Endowment policies combine insurance coverage with a savings
                component. These policies may provide a maturity benefit if the
                insured survives the policy term, subject to policy terms.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-infinity"></Icon>
              <CardTitle>Whole Life Insurance</CardTitle>
              <CardText>
                Whole life insurance provides coverage for the entire lifetime
                of the insured person, subject to policy conditions.
              </CardText>
              <CardText>
                Premiums and benefits may vary depending on the insurer and
                policy structure.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-hand-holding-usd"></Icon>
              <CardTitle>Money Back Policies</CardTitle>
              <CardText>
                Money back policies may provide periodic payouts during the
                policy term along with insurance coverage.
              </CardText>
              <CardText>
                Such policies may combine protection and periodic benefits.
              </CardText>
            </Card>
          </Cards>

          <h2>
            Key Benefits of <span>Life Insurance</span>
          </h2>
          <Cards>
            <Card>
              <Icon className="fas fa-shield-alt"></Icon>
              <CardTitle>Financial Protection</CardTitle>
              <CardText>
                Life insurance can help provide financial support to dependents
                in case of the insured person’s death during the policy term.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-chart-line"></Icon>
              <CardTitle>Long-Term Financial Planning</CardTitle>
              <CardText>
                Certain types of life insurance policies may be considered as
                part of long-term financial planning strategies.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-sliders-h"></Icon>
              <CardTitle>Flexible Coverage Options</CardTitle>
              <CardText>
                Different policy types allow individuals to choose coverage
                based on their financial needs and goals.
              </CardText>
            </Card>
          </Cards>
          <h2>
            Factors That <span>Affect</span> Life Insurance Premiums
          </h2>
          <p>
            The premium for life insurance policies may depend on several
            factors.
          </p>
          <Cards>
            <Card>
              <Icon className="fas fa-user-clock"></Icon>
              <CardTitle>Age</CardTitle>
              <CardText>
                Younger applicants may have lower premiums compared to older
                individuals.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-heartbeat"></Icon>
              <CardTitle>Health Condition</CardTitle>
              <CardText>
                Medical history and lifestyle habits may affect premium rates.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-layer-group"></Icon>
              <CardTitle>Coverage Amount</CardTitle>
              <CardText>
                Higher coverage amounts generally result in higher premiums.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-clipboard-list"></Icon>
              <CardTitle>Policy Type</CardTitle>
              <CardText>
                Different life insurance policies have different premium
                structures depending on the coverage and benefits offered.
              </CardText>
            </Card>
          </Cards>
          <p>The deduction may apply when policies are purchased for:</p>
          <ul>
            <li>Self</li>
            <li>Spouse</li>
            <li>Children</li>
          </ul>

          <h5>Section 10(10D) – Tax Exemption on Benefits</h5>
          <p>
            Under certain conditions, the maturity proceeds or death benefits
            from life insurance policies may be exempt from tax under Section
            10(10D) of the Income Tax Act.
          </p>
          <p>
            Tax treatment depends on policy terms and prevailing tax
            regulations.
          </p>

          <h2>
            Common <span>Mistakes People Make</span> When Buying Life Insurance
          </h2>
          <h5>Not understanding policy terms</h5>
          <p>
            Many individuals purchase insurance without fully understanding
            policy conditions, exclusions, and benefits.
          </p>

          <h5>Choosing coverage without financial planning</h5>
          <p>
            It is important to evaluate financial needs before selecting
            coverage.
          </p>

          <h5>Ignoring long-term commitments</h5>
          <p>
            Life insurance policies often involve long-term commitments, so
            reviewing the policy carefully is important.
          </p>

          <h2>
            Frequently <span>Asked</span> Questions
          </h2>
          <FAQ faqData={faqData} />

          <h5>How Wealthies Helps</h5>
          <p>
            Wealthies aims to simplify insurance awareness by helping
            individuals understand insurance concepts in simple language.
          </p>
          <p>Through our platform, users can:</p>
          <ul>
            <li>Learn about different types of insurance policies</li>
            <li>Understand factors to consider before purchasing coverage</li>
            <li>
              Connect with licensed insurance professionals for further
              assistance
            </li>
            <li>Receive support guidance through Wealthies Shield</li>
          </ul>

          <p>Want to understand which type of insurance may suit your needs?</p>
          <p>
            Our team can help you understand the basics and connect you with
            licensed insurance professionals for further guidance.
          </p>
          <CTAButtonWrapper>
            <CTAButton onClick={openModal}>
              Get Free Insurance Guidance
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </ContentWrapper>
    </>
  );
};

export default LifeInsurance;
